import React, { useState } from "react";
import {
  Users,
  UserCheck,
  DollarSign,
  Upload,
  Flag,
} from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import api from "../../../services/api";
import { getTimeAgo } from "../../../services/helper";
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");

function Avatar({ name, avatar, color }) {
  if (avatar) {
    return (
      <img
          src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
        alt={name}
        className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
      />
    );
  }

  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "??";

  return (
    <div
      className={`w-10 h-10 ${color || "bg-rose-400"} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

export default function Dashboard() {
  const [openPopup, setOpenPopup] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-admin"],
    queryFn: () => api.getDashboardAdminData(),
    
    enabled: true,
    keepPreviousData: true, // Smooth UX when changing pages/search
    // refetchInterval: 30000, // Optional: refresh every 30 seconds
  });

  const statsData = data?.stats || {};
  const activities = data?.data || []; // Show max 10 items

  console.log({statsData});
  console.log({activities});

  
  const stats = [
    {
      label: "Total Users",
      value: statsData.totalUsers?.toLocaleString() || "0",
      icon: Users,
      highlight: false,
    },
    {
      label: "Pilgrim Members",
      value: statsData.pilgrimUsers?.toLocaleString() || "0",
      icon: UserCheck,
      highlight: false,
    },
    {
      label: "Free Users",
      value: statsData.freeUsers?.toLocaleString() || "0",
      icon: Users,
      highlight: false,
    },
    {
      label: "Revenue",
      value: `$${parseFloat(statsData.totalRevenue || 0).toLocaleString()}`,
      icon: DollarSign,
      highlight: true,
    },
  ];

  const getActivityIcon = (type, isFlagged) => {
    if (isFlagged) return Flag;
    if (type?.includes("post") || type?.includes("upload")) return Upload;
    return UserCheck;
  };

  const getAvatarColor = (index) => {
    const colors = [
      "bg-rose-400", "bg-amber-400", "bg-pink-400", "bg-green-400",
      "bg-cyan-400", "bg-violet-400", "bg-yellow-400", "bg-orange-500"
    ];
    return colors[index % colors.length];
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50 sm:p-2 md:p-0 2xl:p-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Dashboard
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Welcome back. Here's what's happening today.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              className={`w-full min-h-[174px] rounded-3xl px-5 sm:px-6 py-6 sm:py-7 transition-all ${
                stat.highlight
                  ? "bg-gradient-to-r from-[#1F3A60] to-[#4078C6] text-white"
                  : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className={`text-[16px] leading-6 font-medium ${stat.highlight ? "text-blue-100" : "text-gray-500"}`}>
                  {stat.label}
                </p>

                <div
                  className={`w-12 h-12 sm:w-[51px] sm:h-[51px] rounded-xl flex items-center justify-center flex-shrink-0 ${
                    stat.highlight ? "bg-white/10" : "bg-gray-100"
                  }`}
                >
                  <Icon size={28} className={stat.highlight ? "text-white" : "text-gray-400"} />
                </div>
              </div>

              <p className={`text-[45px] sm:text-4xl lg:text-[45px] leading-tight font-bold tracking-[-1px] ${stat.highlight ? "text-white" : "text-gray-900"}`}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="overflow-hidden">
        <div className="flex items-center justify-between px-6">
          <h2 className="font-semibold text-gray-900">Recent Activity</h2>
          {/* <button className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
            View All
          </button> */}
        </div>

        <div className="flex flex-col gap-y-4 mt-4">
          {isLoading ? (
            <div className="bg-white rounded-lg px-6 py-8 text-center text-gray-400">
              Loading recent activity...
            </div>
          ) : activities.length === 0 ? (
            <div className="bg-white rounded-lg px-6 py-8 text-center text-gray-400">
              No recent activity.
            </div>
          ) : (
            activities.map((item, index) => {
              const IconComponent = getActivityIcon(item.activity_type, item.is_flagged);
              const isFlagged = item.is_flagged === 1;

              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-4 cursor-pointer rounded-lg px-6 py-4 transition-colors ${
                    isFlagged ? "bg-red-100 hover:bg-red-200" : "bg-white hover:bg-gray-100"
                  }`}
                >
                  <Avatar
                    name={item.name}
                    avatar={item.avatar}
                    color={getAvatarColor(index)}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {isFlagged ? (
                        <span className="text-sm font-semibold text-red-700">
                          🚩 {item.message}
                        </span>
                      ) : (
                        <>
                          <span className="text-sm font-semibold text-gray-900">
                            {item.name}
                          </span>
                          {item.role && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              item.role === "pilgrim"
                                ? "bg-indigo-100 text-indigo-600"
                                : "bg-sky-100 text-sky-600"
                            }`}>
                              {item.role}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 truncate">
                      {item.message}
                    </p>
                  </div>

                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {getTimeAgo(item.created_at)}
                  </span>

                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}